/**
 * Voorraad Item Onderdelen Service - API calls voor onderdeel koppelingen
 * Bijv: Motor → Oliefilter, Membraanpomp → Membraan
 */

import apiFetch from './api';

// Child/Parent item info (basis info van voorraad item)
export interface ItemInfo {
  id: number;
  onderdeel_type: string;
  fabrikant: string | null;
  model: string | null;
  artikelnummer: string | null;
}

// Volledige koppeling response
export interface VoorraadItemOnderdeel {
  id: number;
  parent_item_id: number;
  child_item_id: number;
  onderdeel_type: string | null;
  vervangingsinterval_uren: number | null;
  vervangingsinterval_maanden: number | null;
  aantal: number;
  verplicht: boolean;
  opmerkingen: string | null;
  actief: boolean;
  created_at: string;
  updated_at: string | null;
  child_item?: ItemInfo;
  parent_item?: ItemInfo;
}

// Create/Update interfaces
export interface VoorraadItemOnderdeelCreate {
  parent_item_id: number;
  child_item_id: number;
  onderdeel_type?: string;
  vervangingsinterval_uren?: number;
  vervangingsinterval_maanden?: number;
  aantal?: number;
  verplicht?: boolean;
  opmerkingen?: string;
  actief?: boolean;
}

export interface VoorraadItemOnderdeelUpdate {
  onderdeel_type?: string;
  vervangingsinterval_uren?: number | null;
  vervangingsinterval_maanden?: number | null;
  aantal?: number;
  verplicht?: boolean;
  opmerkingen?: string | null;
  actief?: boolean;
}

export interface VoorraadItemOnderdeelFilters {
  parent_item_id?: number;
  child_item_id?: number;
  actief?: boolean;
}

export const voorraadItemOnderdelenService = {
  /**
   * Haal alle koppelingen op, optioneel gefilterd
   */
  async getAll(filters: VoorraadItemOnderdeelFilters = {}): Promise<VoorraadItemOnderdeel[]> {
    const params = new URLSearchParams();

    if (filters.parent_item_id !== undefined) {
      params.append('parent_item_id', filters.parent_item_id.toString());
    }
    if (filters.child_item_id !== undefined) {
      params.append('child_item_id', filters.child_item_id.toString());
    }
    if (filters.actief !== undefined) {
      params.append('actief', filters.actief.toString());
    }

    const queryString = params.toString();
    const url = `/api/v1/voorraad-item-onderdelen${queryString ? `?${queryString}` : ''}`;

    return apiFetch(url);
  },

  /**
   * Haal een specifieke koppeling op
   */
  async getById(id: number): Promise<VoorraadItemOnderdeel> {
    return apiFetch(`/api/v1/voorraad-item-onderdelen/${id}`);
  },

  /**
   * Haal alle onderdelen op voor een specifiek product
   * Bijv: Alle filters/impellers voor een motor
   */
  async getOnderdelenVoorItem(parentItemId: number, actief: boolean = true): Promise<VoorraadItemOnderdeel[]> {
    const params = new URLSearchParams();
    params.append('actief', actief.toString());
    return apiFetch(`/api/v1/voorraad-item-onderdelen/by-parent/${parentItemId}?${params.toString()}`);
  },

  /**
   * Haal alle producten op waar dit onderdeel bij hoort
   * Bijv: Bij welke motoren past deze oliefilter?
   */
  async getProductenVoorOnderdeel(childItemId: number, actief: boolean = true): Promise<VoorraadItemOnderdeel[]> {
    const params = new URLSearchParams();
    params.append('actief', actief.toString());
    return apiFetch(`/api/v1/voorraad-item-onderdelen/by-child/${childItemId}?${params.toString()}`);
  },

  /**
   * Maak een nieuwe koppeling aan
   */
  async create(data: VoorraadItemOnderdeelCreate): Promise<VoorraadItemOnderdeel> {
    return apiFetch('/api/v1/voorraad-item-onderdelen', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update een bestaande koppeling
   */
  async update(id: number, data: VoorraadItemOnderdeelUpdate): Promise<VoorraadItemOnderdeel> {
    return apiFetch(`/api/v1/voorraad-item-onderdelen/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Verwijder een koppeling
   */
  async delete(id: number): Promise<void> {
    return apiFetch(`/api/v1/voorraad-item-onderdelen/${id}`, {
      method: 'DELETE',
    });
  },
};

export default voorraadItemOnderdelenService;
